import './_inboxPage.scss'

import { useEffect, useMemo, useRef } from 'react'

import { useTranslation } from 'react-i18next'
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom'

import avatarPlaceHolder from 'assets/images/user_placeholder.png'
import { addToast } from 'components/Toast/Toast'
import UserDetails from 'components/UI/Modal/UserDetails/UserDetails'
import { useModalContext } from 'context/modalContext'
import { usePageTitle } from 'context/pageTitleContext'
import { useUserProfile } from 'store'
import type { IProfile } from 'types/interfaces/IProfile'

import { Inbox } from '.'

function InboxPage() {
    const { t } = useTranslation()
    const { id } = useParams()
    const navigate = useNavigate()
    const modalData = useModalContext()
    const userData = useUserProfile()
    const { setTitle } = usePageTitle()
    const location = useLocation()

    // ---- preloaded user from navigation state (Inbox / ChatCard / Conversation) ----
    const locationState = location.state as { otherUserData?: IProfile } | null
    const preloadedOtherUser = locationState?.otherUserData

    const blockedUserId = userData?.blocke_by_model?.map((user: any) => user.id)

    // single source of truth for the other user on this page
    const targetUser: IProfile | undefined = preloadedOtherUser ?? undefined

    // ---- Memoized title element (avatar + name) for /chat/:id ----
    const titleElement = useMemo(() => {
        // If there's no id or no user data, let the caller fall back to default "messages" title
        if (!id || !targetUser) return null

        const handleClick = () => {
            if (!targetUser) return

            if (userData?.role === 'fan') {
                if (blockedUserId?.includes(targetUser.id)) {
                    addToast('error', 'Creator has blocked you.')
                } else {
                    navigate(`/${targetUser.username}`)
                }
            } else {
                modalData.addModal('', <UserDetails user={targetUser} />, true)
            }
        }

        return (
            <div className="title" onClick={handleClick}>
                <div className="other_user_avatar">
                    <img
                        src={
                            !targetUser?.deleted
                                ? targetUser?.avatar?.url ?? avatarPlaceHolder
                                : avatarPlaceHolder
                        }
                        alt="avatar"
                    />
                </div>
                {!targetUser?.deleted
                    ? targetUser?.display_name
                    : t('deletedUser')}
            </div>
        )
    }, [
        id,
        targetUser,
        targetUser?.id,
        targetUser?.avatar?.url,
        targetUser?.deleted,
        targetUser?.display_name,
        targetUser?.username,
        userData?.role,
        Array.isArray(blockedUserId) ? blockedUserId.join(',') : blockedUserId,
        t,
        navigate,
        modalData
    ])

    // ---- Apply page title; fall back to generic "messages" when no target user ----
    const prevTitle = useRef<any>(null)
    useEffect(() => {
        if (id && titleElement) {
            if (prevTitle.current !== titleElement) {
                setTitle(titleElement)
                prevTitle.current = titleElement
            }
        } else {
            // no id or no user → generic "Messages" title
            if (prevTitle.current !== t('messages')) {
                setTitle(t('messages'))
                prevTitle.current = t('messages')
            }
        }
    }, [id, titleElement, t, setTitle])

    return (
        <>
            <Inbox customClass={`inbox-whs ${id ? 'mobile-display-none' : ''}`} />
            <Outlet />
        </>
    )
}

export default InboxPage
